import { useEffect, useState } from "react";
import type {
  Project,
  PublishedVersion,
  Repository,
  SourceParameters,
  Values,
} from "@proof/core";
import { ImpersonationPicker, type PreviewUser } from "./ImpersonationPicker";
import { prepareImpersonation } from "./impersonation-session";
import { previewWindowFeatures } from "./native-preview";
import type { createWorkspaceTransport } from "./workspace-transport";

export interface DesignerTrySnapshot {
  project: Project;
  values: Values;
  partId: string;
  demoProfile: "alex" | "jamie" | "guest";
  sourceParameters?: SourceParameters;
}

/** Reuses the existing impersonation authority; never publishes or invents a preview identity. */
export function DesignerTryItOut({
  snapshot,
  repository,
  transport,
  onClose,
}: {
  snapshot: DesignerTrySnapshot;
  repository: Repository;
  transport: ReturnType<typeof createWorkspaceTransport>;
  onClose(): void;
}) {
  const [data, setData] = useState<{
      users: PreviewUser[];
      versions: PublishedVersion[];
    }>(),
    [loadError, setLoadError] = useState(""),
    [launchError, setLaunchError] = useState(""),
    [versionId, setVersionId] = useState(""),
    [partId, setPartId] = useState(snapshot.partId);
  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setLoadError("");
    setLaunchError("");
    void Promise.all([
      transport.client.previewUsers(controller.signal),
      transport.demo
        ? Promise.resolve([])
        : repository.versions(snapshot.project.tenantId, snapshot.project.id),
    ])
      .then(([users, versions]) => {
        if (controller.signal.aborted) return;
        const latest = versions.at(-1);
        setData({ users, versions });
        setVersionId(latest?.id || "");
        const definition = transport.demo
          ? snapshot.project
          : latest?.definition;
        setPartId(
          definition?.parts.some((part) => part.id === snapshot.partId)
            ? snapshot.partId
            : "",
        );
        if (!transport.demo && !latest)
          setLoadError(
            "No published version is available. Publish this project first, then try it out. Your draft has not been changed.",
          );
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setLoadError(
            `Could not load the impersonation preview: ${(error as Error).message}`,
          );
      });
    return () => controller.abort();
  }, [snapshot, repository, transport]);
  const version = data?.versions.find((row) => row.id === versionId);
  const definition = transport.demo ? snapshot.project : version?.definition;
  return (
    <ImpersonationPicker
      users={data?.users || []}
      loading={!data && !loadError}
      blocked={!data || (!transport.demo && !version)}
      error={loadError || launchError}
      description={
        transport.demo
          ? "Try your current draft in a separate proofing window. Changes in that window stay separate from the designer."
          : "Try a published version in a separate proofing window, starting with its defaults and the selected user's profile. Unsaved designer changes are not included. Your selected user is checked by the existing impersonation flow."
      }
      versions={transport.demo ? undefined : data?.versions || []}
      versionId={versionId}
      onVersionChange={(id) => {
        setVersionId(id);
        setPartId("");
        setLaunchError("");
      }}
      parts={definition?.parts}
      partId={partId}
      onPartChange={setPartId}
      onClose={onClose}
      onSelect={(subject) => {
        setLaunchError("");
        try {
          if (
            !data ||
            (subject !== null &&
              !data.users.some((user) => user.id === subject))
          )
            throw new Error("Choose an available impersonation user.");
          if (!transport.demo) {
            if (!version) throw new Error("Choose a published version first.");
            transport.openPreview(
              {
                projectId: snapshot.project.id,
                versionId: version.id,
                partId,
                values: {},
                previewSubject: subject,
              },
              { newWindow: true },
            );
          } else {
            const popup = window.open(
              "about:blank",
              "_blank",
              previewWindowFeatures(),
            );
            if (!popup)
              throw new Error(
                "Allow popups, then choose Open proofing page to try again.",
              );
            try {
              popup.opener = null;
              popup.location.replace(
                prepareImpersonation({
                  ...snapshot,
                  partId,
                  previewSubject: subject,
                }),
              );
            } catch (error) {
              popup.close();
              throw error;
            }
          }
          onClose();
        } catch (error) {
          setLaunchError((error as Error).message);
        }
      }}
    />
  );
}
